const EVENTS = window.theme.results.events

const SORT_OPTIONS = [
  { field: 'score', order: 'desc', label: 'Relevance', selected: true },
  {
    field: 'price',
    order: 'asc',
    label: 'Price: Low to High',
    selected: false,
  },
  {
    field: 'price',
    order: 'desc',
    label: 'Price: High to Low',
    selected: false,
  },
  { field: 'name', order: 'asc', label: 'Name: A to Z', selected: false },
  { field: 'name', order: 'desc', label: 'Name: Z to A', selected: false },
  { field: 'datePublished', order: 'desc', label: 'Newest', selected: false },
  { field: 'datePublished', order: 'asc', label: 'Oldest', selected: false },
]

class ResultsStateLayer {
  constructor () {
    this.eventBus = window.theme.results.eventBus

    this.state = {
      activeTerm: this.activeTerm,
      activeFacets: [],
      activeSort: { field: 'score', order: 'desc' },
      facets: [],
      pagination: {},
      products: [],
      productsPerPage: window.theme.results.props.productsPerPage,
      sort: SORT_OPTIONS.map(option => ({ ...option })),
      lastInteraction: null,
    }

    this.subscribeToEvents()
    this.initializeStateFromUrl()

    queueMicrotask(() => {
      this.eventBus.publish(EVENTS.API.FETCH_PRODUCTS, {})
    })
  }

  subscribeToEvents () {
    this.eventBus.subscribe(EVENTS.API.PRODUCTS_FETCHED, response =>
      this.onProductsFetched(response),
    )
    this.eventBus.subscribe(EVENTS.API.FACETS_FETCHED, facets =>
      this.updateFacets(facets),
    )
    this.eventBus.subscribe(EVENTS.UI.FACET_CLEAR_ALL, () =>
      this.onFacetClearAll(),
    )
    this.eventBus.subscribe(EVENTS.UI.SORT_CHANGED, sort =>
      this.onSortChanged(sort),
    )
    this.eventBus.subscribe(EVENTS.UI.PRICE_CHANGED, ({ min, max }) =>
      this.onPriceChanged({ min, max }),
    )
    this.eventBus.subscribe(EVENTS.UI.FACET_ADDED, ({ field, value }) =>
      this.onFacetAdded({ field, value }),
    )
    this.eventBus.subscribe(EVENTS.UI.FACET_REMOVED, ({ field, value }) =>
      this.onFacetRemoved({ field, value }),
    )
    this.eventBus.subscribe(EVENTS.STATE.ACTIVE_SORT_UPDATED, activeSort =>
      this.onActiveSortUpdated(activeSort),
    )
    this.eventBus.subscribe(EVENTS.UI.PAGINATION_NEXT, () =>
      this.onPaginationNext(),
    )
  }

  async onProductsFetched (response) {
    const pagination = {
      total: response.products.total,
      size: response.products.size,
      from: response.products.from,
      totalPages: Math.ceil(response.products.total / response.products.size),
      currentPage: Math.ceil(
        (response.products.from + 1) / response.products.size,
      ),
      showNextButton:
        response.products.from + response.products.size <
        response.products.total,
      showPreviousButton: response.products.from > 0,
    }

    this.updatePagination({ ...this.state.pagination, ...pagination })

    let normalized = response.products.hits
    let storeDefaultCurrency = 'GBP'

    if (window.Shopify.shop === 'oh-polly-us.myshopify.com') {
      storeDefaultCurrency = 'USD'
    }

    if (window.Shopify.shop === 'oh-polly-au.myshopify.com') {
      storeDefaultCurrency = 'AUD'
    }

    if (window.Shopify.shop === 'oh-polly-ae.myshopify.com') {
      storeDefaultCurrency = 'AED'
    }

    if (window.Shopify.currency.active !== storeDefaultCurrency) {
      normalized = await this.updateWithShopifyPrices(response.products.hits)
    }

    this.updateProducts(normalized)

    if (this.activeTerm.length > 0) {
      window.theme.results.eventBus.publish(EVENTS.STATE.SEARCH_TOTAL_UPDATED, {
        total: this.state.pagination.total,
        activeTerm: this.activeTerm,
      })
    }
  }

  async updateWithShopifyPrices (hits) {
    const results = await Promise.allSettled(
      hits.map(hit => {
        const handle = hit.url
          ?.split('?')[0]
          .split('/')
          .pop()
        return handle
          ? window.BAO.utils.fetchProductData(handle)
          : Promise.reject(new Error('missing handle'))
      }),
    )

    return hits.map((hit, i) => {
      const settled = results[i]
      if (settled.status !== 'fulfilled') return hit

      const data = settled.value?.product
      if (!data) return hit

      const priceCents = data.price ?? data.variants?.[0]?.price
      const compareCents =
        data.compare_at_price ?? data.variants?.[0]?.compare_at_price

      if (priceCents == null) return hit

      return {
        ...hit,
        price: priceCents,
        listPrice: compareCents,
      }
    })
  }

  onFacetClearAll () {
    this.state.lastInteraction = 'filter'
    this.state.pagination.currentPage = 1
    this.state.pagination.from = 0
    this.updateActiveFacets([])
    this.updateActiveSort({ field: 'score', order: 'desc' })
    this.syncUrlAndFetch()
  }

  onSortChanged ({ field, order }) {
    this.state.lastInteraction = 'sort'
    this.state.pagination.currentPage = 1
    this.state.pagination.from = 0
    this.updateActiveSort({ field, order })
    this.syncUrlAndFetch()
  }

  onPriceChanged ({ min, max }) {
    this.state.lastInteraction = 'filter'
    this.state.pagination.currentPage = 1
    this.state.pagination.from = 0
    const field = 'price'
    const bounds = this.getPriceStatBoundsFromFacets(this.state.facets)
    let outMin = min
    let outMax = max
    const mn = parseFloat(min)
    const mx = parseFloat(max)
    if (bounds && Number.isFinite(mn) && Number.isFinite(mx)) {
      let lo = Math.min(Math.max(mn, bounds.min), bounds.max)
      let hi = Math.min(Math.max(mx, bounds.min), bounds.max)
      if (lo > hi) {
        const t = lo
        lo = hi
        hi = t
      }
      outMin = String(lo)
      outMax = String(hi)
    }
    const value = [`${outMin}~${outMax}`]
    const existing = this.state.activeFacets.find(
      facet => facet.field === field,
    )

    if (existing) {
      this.updateActiveFacets(
        this.state.activeFacets.map(facet =>
          facet.field === field ? { field, value } : facet,
        ),
      )
    } else {
      this.updateActiveFacets([...this.state.activeFacets, { field, value }])
    }

    this.syncUrlAndFetch(undefined, { debounceMs: 300 })
  }

  onFacetAdded ({ field, value }) {
    this.state.lastInteraction = 'filter'
    this.state.pagination.currentPage = 1
    this.state.pagination.from = 0
    const existing = this.state.activeFacets.find(
      facet => facet.field === field,
    )

    if (existing) {
      const merged = [...new Set([...existing.value, ...value])]
      this.updateActiveFacets(
        this.state.activeFacets.map(facet =>
          facet.field === field ? { field, value: merged } : facet,
        ),
      )
    } else {
      this.updateActiveFacets([...this.state.activeFacets, { field, value }])
    }

    this.syncUrlAndFetch()
  }

  onFacetRemoved ({ field, value }) {
    this.state.lastInteraction = 'filter'
    this.state.pagination.currentPage = 1
    this.state.pagination.from = 0
    const existing = this.state.activeFacets.find(
      facet => facet.field === field,
    )

    if (existing) {
      const filtered = existing.value.filter(v => !value.includes(v))
      if (filtered.length > 0) {
        this.updateActiveFacets(
          this.state.activeFacets.map(facet =>
            facet.field === field ? { field, value: filtered } : facet,
          ),
        )
      } else {
        this.updateActiveFacets(
          this.state.activeFacets.filter(facet => facet.field !== field),
        )
      }
    }

    this.syncUrlAndFetch()
  }

  onActiveSortUpdated (activeSort) {
    this.state.sort.forEach(sort => {
      sort.selected =
        sort.field === activeSort.field && sort.order === activeSort.order
    })
  }

  onPaginationChanged (cursor) {
    this.state.lastInteraction = 'pagination'
    this.state.pagination.from = Number(cursor.current)
    if (cursor.direction === 'next') this.state.pagination.currentPage += 1
    else if (cursor.direction === 'previous') {
      this.state.pagination.currentPage -= 1
    }
    this.syncUrlAndFetch(cursor.direction)
  }

  onPaginationNext () {
    this.state.lastInteraction = 'pagination'
    this.state.pagination.from += this.state.pagination.size
    this.state.pagination.currentPage += 1
    this.syncUrlAndFetch('next')
  }

  initializeStateFromUrl () {
    const searchParams = new URLSearchParams(window.location.search)
    const activeFacets = []

    searchParams.forEach((value, key) => {
      const values = value.includes(',') ? value.split(',') : [value]

      switch (key) {
        case 'sort': {
          const [field, order] = values[0].split('~')
          this.updateActiveSort({ field, order })
          break
        }
        case 'page': {
          const currentPage = parseInt(value, 10)
          this.state.pagination.currentPage = currentPage
          this.state.pagination.from =
            (currentPage - 1) * this.state.productsPerPage
          break
        }
        case 'q':
          this.state.activeTerm = value
          break
        default: {
          const isFacetField =
            key === 'price' ||
            key === 'available' ||
            key.includes('customFields.')
          if (isFacetField) activeFacets.push({ field: key, value: values })
        }
      }
    })

    this.updateActiveFacets(activeFacets)
  }

  buildSearchParams () {
    const params = new URLSearchParams()

    if (this.state.activeTerm) {
      params.set('q', this.state.activeTerm)
    }

    this.state.activeFacets.forEach(facet => {
      if (facet.value.length > 0) {
        params.set(facet.field, facet.value.join(','))
      }
    })

    if (this.state.activeSort) {
      params.set(
        'sort',
        `${this.state.activeSort.field}~${this.state.activeSort.order}`,
      )
    }

    if (this.state.pagination.currentPage > 1) {
      params.set('page', this.state.pagination.currentPage)
    }

    return params
  }

  syncUrlAndFetch (direction, { debounceMs = 0 } = {}) {
    const runSync = () => {
      const searchParams = this.buildSearchParams()
      const query = searchParams.toString()
      const url = query
        ? `${window.location.pathname}?${query}`
        : window.location.pathname
      window.history.replaceState({}, '', url)
      this.eventBus.publish(EVENTS.API.FETCH_PRODUCTS, {
        targetPage: this.state.pagination.currentPage,
        direction,
      })
    }

    clearTimeout(this.syncTimeout)

    if (debounceMs > 0) {
      this.syncTimeout = setTimeout(runSync, debounceMs)
      return
    }

    runSync()
  }

  updateProducts (products) {
    this.state.products = products
    this.eventBus.publish(EVENTS.STATE.PRODUCTS_UPDATED, {
      products,
      interaction: this.state.lastInteraction || 'initial',
    })
    this.state.lastInteraction = null
  }

  updateFacets (facets) {
    this.state.facets = facets
    this.clampActivePriceToCurrentFacetStats(facets)
    this.eventBus.publish(EVENTS.STATE.FACETS_UPDATED, {
      facets,
      activeFacets: this.state.activeFacets,
    })
  }

  getPriceStatBoundsFromFacets (facets) {
    if (!Array.isArray(facets)) return null
    const f = facets.find(
      x =>
        x &&
        x.field === 'price' &&
        typeof x.max === 'number' &&
        Number.isFinite(x.max),
    )
    if (!f) return null
    const low = Number.isFinite(f.min) ? Math.floor(f.min) : 0
    const high = Math.ceil(f.max)
    if (high <= 0) return null
    return { min: Math.max(0, low), max: high }
  }

  replaceSearchUrlFromState () {
    const searchParams = this.buildSearchParams()
    const query = searchParams.toString()
    const url = query
      ? `${window.location.pathname}?${query}`
      : window.location.pathname
    window.history.replaceState({}, '', url)
  }

  clampActivePriceToCurrentFacetStats (facets) {
    const bounds = this.getPriceStatBoundsFromFacets(facets)
    if (!bounds) return

    const idx = this.state.activeFacets.findIndex(x => x.field === 'price')
    if (idx === -1) return

    const raw = this.state.activeFacets[idx].value?.[0]
    if (typeof raw !== 'string' || !raw.includes('~')) return

    const parts = raw.split('~')
    if (parts.length !== 2) return

    const lo = parseFloat(parts[0])
    const hi = parseFloat(parts[1])
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) return

    let loC = Math.min(Math.max(lo, bounds.min), bounds.max)
    let hiC = Math.min(Math.max(hi, bounds.min), bounds.max)
    if (loC > hiC) {
      const t = loC
      loC = hiC
      hiC = t
    }

    if (loC === lo && hiC === hi) return

    const next = this.state.activeFacets.map((facet, i) =>
      i === idx ? { field: 'price', value: [`${loC}~${hiC}`] } : facet,
    )
    this.updateActiveFacets(next)
    this.replaceSearchUrlFromState()
  }

  updateActiveFacets (activeFacets) {
    this.state.activeFacets = activeFacets
    this.eventBus.publish(EVENTS.STATE.ACTIVE_FACETS_UPDATED, { activeFacets })
  }

  updateActiveSort (activeSort) {
    this.state.activeSort = activeSort
    this.eventBus.publish(EVENTS.STATE.ACTIVE_SORT_UPDATED, activeSort)
  }

  updatePagination (pagination) {
    this.state.pagination = pagination
    this.eventBus.publish(EVENTS.STATE.PAGINATION_UPDATED, pagination)
  }

  get activeTerm () {
    const searchParams = new URLSearchParams(window.location.search)
    return searchParams.get('q') || ''
  }
}

// Initialize and attach to window for debugging
window.theme.results.stateLayer = new ResultsStateLayer()
